import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTeamMembers } from '../hooks/useSupabase';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import PlayerModal from './PlayerModal';
import ShareButton from './ShareButton';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiStar } = FiIcons;

const PlayerCarousel = () => {
  const { data: allPlayers, loading } = useTeamMembers(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [visibleCards, setVisibleCards] = useState(4);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const players = (allPlayers || [])
    .filter(
      (player) =>
        !player.is_staff &&
        Array.isArray(player.team_categories) &&
        player.team_categories.includes('Senior') &&
        player.name
    )
    .sort((a, b) => {
      const soA = a.sort_order ?? 0;
      const soB = b.sort_order ?? 0;
      if (soA !== soB) return soA - soB;
      const numA = a.number ?? 999;
      const numB = b.number ?? 999;
      if (numA !== numB) return numA - numB;
      return (a.name || '').localeCompare(b.name || '');
    });

  const hasPlayers = players && players.length > 0;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setVisibleCards(1);
      else if (window.innerWidth < 768) setVisibleCards(2);
      else if (window.innerWidth < 1024) setVisibleCards(3);
      else setVisibleCards(4);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const effectiveVisibleCards = hasPlayers ? Math.min(visibleCards, players.length) : 1;
  const totalSlides = hasPlayers ? Math.ceil(players.length / effectiveVisibleCards) : 1;

  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 3000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-primary-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Φόρτωση παικτριών...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!hasPlayers) return null;

  const startIndex = currentSlide * effectiveVisibleCards;
  const endIndex = startIndex + effectiveVisibleCards;
  const visiblePlayers = players.slice(startIndex, endIndex);

  const nextSlide = () => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    if (totalSlides <= 1) return;
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => setCurrentSlide(index);

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlayer(null);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary-50 to-white overflow-visible">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-visible">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Οι Παίκτριες μας</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Γνωρίστε τις ταλαντούχες παίκτριες της Α' Ομάδας του ΑΟΧ
          </p>
        </motion.div>

        <div className="relative overflow-visible">
          {totalSlides > 1 && (
            <>
              <button
                onClick={prevSlide}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200 flex items-center justify-center group hover:scale-110"
              >
                <SafeIcon icon={FiChevronLeft} className="w-6 h-6 text-gray-600 group-hover:text-primary-600 transition-colors duration-200" />
              </button>
              <button
                onClick={nextSlide}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all duration-200 flex items-center justify-center group hover:scale-110"
              >
                <SafeIcon icon={FiChevronRight} className="w-6 h-6 text-gray-600 group-hover:text-primary-600 transition-colors duration-200" />
              </button>
            </>
          )}

          <div
            className="mx-12 overflow-visible"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="overflow-visible">
              <div className="flex overflow-visible">
                {visiblePlayers.map((player, index) => (
                  <motion.div
                    key={player.id}
                    className="flex-shrink-0 px-3 cursor-pointer overflow-visible"
                    style={{ width: `${100 / effectiveVisibleCards}%` }}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    onClick={() => handlePlayerClick(player)}
                  >
                    <div className="group relative h-full overflow-visible">
                      <div className="bg-white rounded-2xl shadow-lg overflow-visible hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full relative">
                        <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 rounded-t-2xl">
                          <ImageWithFallback
                            src={player.image_url}
                            imageWidth={640}
                            alt={player.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                          {player.number && (
                            <div className="absolute top-4 right-4 w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                              {player.number}
                            </div>
                          )}

                          {player.is_featured && (
                            <div className="absolute top-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center">
                              <SafeIcon icon={FiStar} className="w-3 h-3 mr-1" />
                              {player.featured_label || 'ΤΑΛΕΝΤΟ'}
                            </div>
                          )}

                          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-800">
                            {player.position}
                          </div>
                        </div>

                        <div className="hidden sm:block absolute top-1/2 right-4 z-[100] -translate-y-1/2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300" onClick={(e) => e.stopPropagation()}>
                          <ShareButton
                            url={`${window.location.origin}/share/player/${player.id}`}
                            title={`${player.name} - Παίκτρια ΑΟΧ`}
                            description={`${player.position}${player.age ? ` - ${player.age} ετών` : ''}. ${player.bio ? player.bio.substring(0, 100) : ''}`}
                            imageUrl={player.image_url || ''}
                            compact={true}
                            variant="outline"
                            direction="up"
                            align="right"
                            className="bg-white/95 backdrop-blur-sm hover:bg-white border-white/50 text-gray-700 shadow-xl"
                          />
                        </div>

                        <div className="p-6 rounded-b-2xl bg-white">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors duration-200">
                              {player.name}
                            </h3>
                            <div className="sm:hidden relative z-[100]" onClick={(e) => e.stopPropagation()}>
                              <ShareButton
                                url={`${window.location.origin}/share/player/${player.id}`}
                                title={`${player.name} - Παίκτρια ΑΟΧ`}
                                description={`${player.position}${player.age ? ` - ${player.age} ετών` : ''}. ${player.bio ? player.bio.substring(0, 100) : ''}`}
                                imageUrl={player.image_url || ''}
                                compact={true}
                                variant="text"
                                iconSize="sm"
                                direction="up"
                                align="right"
                                className="text-gray-500 hover:text-primary-600"
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-sm text-gray-600">
                            <span className="font-medium">{player.position}</span>
                            {player.age && <span className="bg-gray-100 px-2 py-1 rounded-full">{player.age} ετών</span>}
                          </div>
                          {player.bio && <p className="mt-3 text-sm text-gray-600 line-clamp-2">{player.bio}</p>}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {totalSlides > 1 && (
            <div className="flex justify-center mt-8 space-x-2">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${index === currentSlide ? 'bg-primary-600 scale-125' : 'bg-gray-300 hover:bg-gray-400'}`}
                />
              ))}
            </div>
          )}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="text-center mt-12">
          <Link to="/team" className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl">
            Δείτε όλες τις Παίκτριες
            <SafeIcon icon={FiChevronRight} className="ml-2 w-5 h-5" />
          </Link>
        </motion.div>
      </div>

      <AnimatePresence>{isModalOpen && <PlayerModal player={selectedPlayer} isOpen={isModalOpen} onClose={closeModal} />}</AnimatePresence>
    </section>
  );
};

export default PlayerCarousel;
